<script lang="ts">
  import { type CueEnterEvent, type CueExitEvent, PlayerEvent, type PlayerInterface } from '@foundation-player/loader';
  import { onDestroy, onMount, getContext, hasContext } from 'svelte';
  import type { PlayerStateContext } from '../types/player-state';
  import type { MediaMode } from '../types/media-mode';

  export let player: PlayerInterface;

  let currentTextTrackCue: CueEnterEvent | null = null;
  let language: string | undefined = undefined;

  const { activeTextTrack } = getContext<PlayerStateContext>('playerState');

  const mode: MediaMode = hasContext('mediaMode') ? getContext<MediaMode>('mediaMode') : 'video';

  $: language = $activeTextTrack?.language || undefined;

  const onCueEnter = (event: CueEnterEvent) => {
    currentTextTrackCue = event;
  };

  const onCueExit = (_event: CueExitEvent) => {
    currentTextTrackCue = null;
  };

  onMount(() => {
    player.addEventListener(PlayerEvent.CueEnter, onCueEnter);
    player.addEventListener(PlayerEvent.CueExit, onCueExit);
  });

  onDestroy(() => {
    player.removeEventListener(PlayerEvent.CueEnter, onCueEnter);
    player.removeEventListener(PlayerEvent.CueExit, onCueExit);
  });

  // We want to reduce jumping of the content. Initially, we reserve space for
  // two lines. If however this is not enough, we increase the space step by
  // step to what is needed. We do not reduce it again.
  // So this might happen:
  //
  // - In the beginning, there is one line of text. We have enough space.
  // - Another cue might need two lines. We still have enough space.
  // - A cue with three lines happens. We increase the reserved space.
  // - The next cue only needs two lines. We keep the reserved space.
  // - The next cue needs three lines again. We now have enough space.
  //
  // Since the needed space depends on the font-size, we reset the reserved
  // space when the width of the element changes (the font size depends on the
  // width of the element).
  let element: HTMLElement | undefined;
  let layerWidth = 320;
  let layerHeight = 32;
  let fontSize = 16;
  let layerMinHeight = 0;

  $: if (mode === 'audio' && layerWidth && element) {
    fontSize = parseFloat(getComputedStyle(element).fontSize);
    layerMinHeight = 2 * fontSize;
  }

  $: if (mode === 'audio') {
    layerMinHeight = layerHeight;
  }
</script>

{#if $activeTextTrack}
  <div
    class="text-track"
    bind:offsetWidth={layerWidth}
    bind:offsetHeight={layerHeight}
    bind:this={element}
    style:min-height="{layerMinHeight}px"
  >
    {#if currentTextTrackCue}
      <div class="text-track-text-wrapper">
        <span data-testid="text-track-text" class="text-track-text" lang={language}>
          {currentTextTrackCue.text}
        </span>
      </div>
    {/if}
  </div>
{/if}

<style module>
  .text-track {
    margin: 0;
    padding: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    pointer-events: none;
    font-size: 1.5em; /* Fallback for older browsers */
    /* Min value (16px) ends at 400 px. Max value (28px) starts at 800 px. */
    /* https://modern-fluid-typography.vercel.app/ */
    font-size: clamp(1em, 3cqw + 0.25em, 1.75em);
    line-height: 1.5;
  }

  .text-track-text-wrapper {
    /* to be able to only have a background behind the text and not the container */
    display: inline-block;
    text-align: center;
    max-width: 100ch;
  }

  .text-track-text {
    background-color: rgba(0, 0, 0, 0.8);
    color: #ffffff;
    font-weight: 500;
    padding: 5px 10px;
  }

  /*
    In audio mode, we do not have enough space within the regular area of the
    player. Besides, we always show the controls, so they overlap with the text
    tracks.
    As a solution, when active, we show the text tracks on top of the player
    with a minimum height to be able to show two lines.
    If more than two lines are needed, we show them. However, the size of the UI
    will jump around.
   */
  :global([data-mode='audio']) .text-track {
    position: static;
    bottom: initial;
    min-height: calc(3em + 10px); /* two lines */
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
  }

  @media (prefers-contrast: more), (prefers-reduced-transparency: reduce) {
    .text-track-text {
      background-color: #000000;
    }
  }
</style>
